import classNames from 'classnames';
import type { ReactElement, Ref, RefObject } from 'react';
import { useCallback, useRef } from 'react';
import styles from './CircleButton.module.scss';
import { setupTransitionInTimeline, setupTransitionOutTimeline } from './CircleButton.transitions';
import { useChangeTransitionState } from 'util/react-transition-component/hooks/useChangeTransitionState';
import { useScrollTransition } from 'util/react-transition-component';
import type { CircleButtonProps, CircleButtonRef } from './CircleButton.data';
import { renderCta } from 'util/cta/cta.template';
import type { CtaRefType } from 'util/cta/cta.data';
import Text from '../text/Text';
import useSetupTimeline from '../../../hooks/useSetupTimeline';
import { setupHoverTransitionTimeline } from '../primary-button/PrimaryButton.transitions';
import Icon from '../icon/Icon';

export default function CircleButton({
  isVisible,
  revertColor = false,
  hasOutline = false,
  isSelected = false,
  disableTriggerInOnScroll = false,
  backgroundHoverOpacity = 0.75,
  iconOnHover = true,
  ref,
  ...props
}: CircleButtonProps & { ref?: Ref<CircleButtonRef> }): ReactElement {
  const localRef = useRef<CtaRefType>(undefined);
  const elementRef = (ref || localRef) as RefObject<CtaRefType | null>;
  const labelRef = useRef<HTMLSpanElement>(null);
  const backgroundHoverRef = useRef<HTMLDivElement>(null);
  const hoverLabelRef = useRef<HTMLSpanElement>(null);
  const hoverTimeline = useRef<gsap.core.Timeline>(undefined);
  let reverted = revertColor;
  if (isSelected) {
    reverted = !revertColor;
  }

  hoverTimeline.current = useSetupTimeline(setupHoverTransitionTimeline, {
    backgroundHoverRef,
    hoverLabelRef,
    labelRef,
  });

  const onMouseLeave = useCallback(() => {
    hoverTimeline?.current?.reverse();
  }, [hoverTimeline]);

  const onMouseEnter = useCallback(() => {
    hoverTimeline?.current?.play();
  }, [hoverTimeline]);

  const { transitionController } = useScrollTransition({
    scroll: {
      disableTriggerInOnScroll,
      trigger: elementRef,
    },
    setupOptions: () => ({
      ref: elementRef,
      refs: {
        elementRef,
        labelRef,
      },
      setupTransitionInTimeline,
      setupTransitionOutTimeline,
    }),
  });

  useChangeTransitionState(transitionController, isVisible);

  return renderCta({
    innerTemplate: (
      <>
        <div
          ref={backgroundHoverRef}
          className={classNames(styles.backgroundHover)}
          style={{ '--background-hover-opacity': backgroundHoverOpacity } as React.CSSProperties}
        />

        <div className={classNames(styles.textHolder)}>
          {props.label && (
            <>
              <Text
                ref={labelRef}
                transitionInType="splitCharsSlideInUp"
                className={classNames(styles.label)}
                as="span"
                size="button-label-large"
              >
                {props.label}
              </Text>
              <Text
                ref={hoverLabelRef}
                transitionInType="splitCharsNone"
                className={classNames(styles.label, styles.hoverLabel)}
                as="span"
                size="button-label-large"
              >
                {props.label}
              </Text>
            </>
          )}

          {props.icon && (
            <>
              <Icon ref={labelRef} name={props.icon} className={classNames(styles.icon)} />
              {iconOnHover && (
                <Icon
                  ref={hoverLabelRef}
                  name={props.icon}
                  className={classNames(styles.icon, styles.hoverLabel)}
                />
              )}
            </>
          )}
        </div>
      </>
    ),
    props: {
      ...props,
      className: classNames(styles.circleButton, props.className, {
        [styles.revertColor]: reverted,
        [styles.hasOutline]: hasOutline,
        [styles.isSelected]: isSelected,
      }),
      onMouseEnter,
      onMouseLeave,
    },
    elementRef,
  });
}
